import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, XCircle, Clock } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { useTranslation } from '../../i18n/LocaleContext';

const AdminDeposits = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });

  useEffect(() => {
    fetchDeposits();
  }, [filter, page]);

  const fetchDeposits = async () => {
    try {
      setLoading(true);
      const data = await adminService.getDeposits(filter, 50, page);
      setDeposits(data.deposits || []);
      setPagination(data.pagination || { total: 0, total_pages: 1 });
    } catch (error) {
      console.error('Fetch deposits error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleApprove = async (depositId) => {
    if (!window.confirm(t('aDepApproveConfirm'))) return;

    try {
      setProcessing(depositId);
      await adminService.approveDeposit(depositId);
      alert(t('aDepApproved'));
      fetchDeposits();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (depositId) => {
    if (!window.confirm(t('aDepRejectConfirm'))) return;

    try {
      setProcessing(depositId);
      await adminService.rejectDeposit(depositId, '');
      alert(t('aDepRejected'));
      fetchDeposits();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-900 bg-opacity-30 border border-yellow-700', text: 'text-yellow-400', label: t('aDepPending'), icon: Clock },
      approved: { bg: 'bg-green-900 bg-opacity-30 border border-green-700', text: 'text-green-400', label: t('aDepApprovedLabel'), icon: CheckCircle },
      rejected: { bg: 'bg-red-900 bg-opacity-30 border border-red-700', text: 'text-red-400', label: t('aDepRejectedLabel'), icon: XCircle }
    };
    const badge = badges[status];
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };


  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">{t('aDepTitle')}</h1>
        <p className="text-gray-400 mt-1">{t('aDepSubtitle')}</p>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl shadow-sm p-4 mb-6 border border-gray-700">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => { setFilter('all'); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {t('aDepAll')}
          </button>
          <button
            onClick={() => { setFilter('pending'); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'pending' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {t('aDepPending')}
          </button>
          <button
            onClick={() => { setFilter('approved'); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'approved' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {t('aDepApprovedLabel')}
          </button>
          <button
            onClick={() => { setFilter('rejected'); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'rejected' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {t('aDepRejectedLabel')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aDepUser')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aDepAmount')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aDepMethod')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aDepStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aDepDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aDepActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                    {t('aDepLoading')}
                  </td>
                </tr>
              ) : deposits.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                    {t('aDepEmpty')}
                  </td>
                </tr>
              ) : deposits.map((deposit) => (
                <tr key={deposit.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 text-sm text-gray-300">#{deposit.id}</td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-white">{deposit.username}</p>
                      <p className="text-xs text-gray-400">{deposit.phone}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-white">{formatCurrency(deposit.amount)}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{deposit.payment_method || t('aDepDefaultMethod')}</td>
                  <td className="px-6 py-4">{getStatusBadge(deposit.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{new Date(deposit.created_at).toLocaleString('vi-VN')}</td>
                  <td className="px-6 py-4">
                    {deposit.status === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(deposit.id)}
                          disabled={processing === deposit.id}
                          className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs hover:bg-green-600 disabled:opacity-50"
                        >
                          {processing === deposit.id ? t('aDepProcessing') : t('aDepApprove')}
                        </button>
                        <button
                          onClick={() => handleReject(deposit.id)}
                          disabled={processing === deposit.id}
                          className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 disabled:opacity-50"
                        >
                          {t('aDepRejectedLabel')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {pagination.total > 0 && <div className="mt-4 flex items-center justify-between text-sm text-gray-400"><span>{t('aDepPaginationTotal', { total: pagination.total, page, pages: pagination.total_pages })}</span><div className="flex gap-2"><button onClick={() => setPage(page - 1)} disabled={page === 1} className="rounded bg-gray-700 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">{t('aDepPrev')}</button><button onClick={() => setPage(page + 1)} disabled={page >= pagination.total_pages} className="rounded bg-gray-700 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">{t('aDepNext')}</button></div></div>}
    </div>
  );
};

export default AdminDeposits;
